// src/pages/QbusAutomations/AutomationList.jsx
// Table of automations with toggle, edit, test, delete, export actions
import React, { useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  History as LogIcon,
  ErrorOutline as ErrorIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon
} from '@mui/icons-material';
import { qbusApi } from '../../services/apiClient';
import ExecutionLogDialog from './ExecutionLogDialog';

const SCOPE_COLORS = {
  system: 'secondary',
  site: 'primary',
  unit: 'default',
};

const AutomationList = ({ automations, onEdit, onToggle, onDelete, onTest, onImport, showScope }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [logTarget, setLogTarget] = useState(null);
  const importInputRef = useRef(null);

  // Export a single automation
  const handleExport = async (auto) => {
    try {
      const response = await qbusApi.getAutomation(auto.id);
      const full = response.data.automation;
      const exportData = {
        name: full.name,
        description: full.description || '',
        flow_definition: full.flow_definition,
        _exportedAt: new Date().toISOString(),
        _version: 1,
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `automation-${full.name.replace(/[^a-zA-Z0-9]/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Import automation from JSON file
  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.flow_definition?.nodes || !data.flow_definition?.edges) {
          alert('Invalid automation file: missing flow_definition.nodes or edges');
          return;
        }
        onImport?.(data);
      } catch (err) {
        alert('Failed to parse automation file: ' + err.message);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  if (automations.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          No automations yet. Click "New Automation" to create one.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ImportIcon />}
          onClick={() => importInputRef.current?.click()}
          size="small"
        >
          Import Automation
        </Button>
        <input
          ref={importInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </Box>
    );
  }

  return (
    <>
      {/* Import button above table */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ImportIcon />}
          onClick={() => importInputRef.current?.click()}
          size="small"
        >
          Import Automation
        </Button>
        <input
          ref={importInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {showScope && <TableCell>Scope</TableCell>}
              <TableCell>Enabled</TableCell>
              <TableCell>Last Triggered</TableCell>
              <TableCell>Runs</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {automations.map((auto) => (
              <TableRow key={auto.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {auto.name}
                    </Typography>
                    {auto.description && (
                      <Typography variant="caption" color="text.secondary">
                        {auto.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {showScope && (
                  <TableCell>
                    <Chip
                      label={auto.scope || 'site'}
                      size="small"
                      color={SCOPE_COLORS[auto.scope] || 'default'}
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Switch
                    size="small"
                    checked={auto.enabled}
                    onChange={() => onToggle(auto.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {auto.last_triggered_at
                      ? new Date(auto.last_triggered_at).toLocaleString()
                      : 'Never'
                    }
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={auto.trigger_count || 0} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {auto.last_error ? (
                    <Tooltip title={auto.last_error}>
                      <Chip
                        icon={<ErrorIcon />}
                        label="Error"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Chip label="OK" size="small" color="success" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit flow">
                    <IconButton size="small" onClick={() => onEdit(auto.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Test run (dry run)">
                    <IconButton size="small" onClick={() => onTest(auto.id)}>
                      <TestIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Execution logs">
                    <IconButton size="small" onClick={() => setLogTarget(auto)}>
                      <LogIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export as JSON">
                    <IconButton size="small" onClick={() => handleExport(auto)}>
                      <ExportIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(auto)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete automation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
            This will also delete all execution logs.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              onDelete(deleteTarget.id);
              setDeleteTarget(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execution Log Dialog */}
      {logTarget && (
        <ExecutionLogDialog
          automationId={logTarget.id}
          automationName={logTarget.name}
          open={!!logTarget}
          onClose={() => setLogTarget(null)}
        />
      )}
    </>
  );
};

export default AutomationList;
